import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, Loader2, Pencil } from "lucide-react";

const PERMS = [
  { key: "can_edit", label: "Kayıt Ekleme/Düzenleme" },
  { key: "can_delete", label: "Kayıt Silme" },
  { key: "can_import", label: "Excel İçe Aktarma" },
  { key: "can_export", label: "Excel Dışa Aktarma" },
  { key: "can_manage_columns", label: "Sütun Yönetimi" },
  { key: "can_manage_notes", label: "Not Yönetimi (sil/düzenle)" },
  { key: "can_manage_users", label: "Kullanıcı Yönetimi" },
];

const ROLES = [
  { value: "admin", label: "Yönetici" },
  { value: "user", label: "Kullanıcı" },
];

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "user",
  permissions: { can_edit: true, can_export: true },
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      permissions: u.permissions || {},
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          name: form.name,
          role: form.role,
          permissions: form.permissions,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/users", form);
        toast.success("Kullanıcı eklendi");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.name} silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const togglePerm = (key) =>
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  return (
    <div className="flex-1 overflow-auto grid-scroll p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Kullanıcılar</h1>
          <p className="text-sm text-muted-foreground">Kullanıcı ekleyin ve yetkilerini yönetin.</p>
        </div>
        <Button onClick={openNew} data-testid="add-user-button">
          <UserPlus className="w-4 h-4 mr-1.5" /> Yeni Kullanıcı
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u) => (
            <Card key={u.id} className="p-5" data-testid={`user-card-${u.email}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-primary text-primary-foreground grid place-items-center font-semibold">
                    {(u.name || "K").slice(0, 2).toUpperCase()}
                  </span>
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md ${
                    u.role === "admin"
                      ? "bg-primary/15 text-primary"
                      : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {u.role === "admin" && <Shield className="w-3 h-3" />}
                  {u.role === "admin" ? "Yönetici" : "Kullanıcı"}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {u.role === "admin" ? (
                  <span className="text-xs text-muted-foreground">Tüm yetkiler</span>
                ) : (
                  PERMS.filter((p) => u.permissions?.[p.key]).map((p) => (
                    <span key={p.key} className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">
                      {p.label}
                    </span>
                  ))
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => remove(u)}
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Ad Soyad</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>E-posta</Label>
              <Input
                type="email"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Yeni Şifre (opsiyonel)" : "Şifre"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.role !== "admin" && (
              <div className="space-y-2">
                <Label>Yetkiler</Label>
                <div className="space-y-2 rounded-lg border border-border p-3">
                  {PERMS.map((p) => (
                    <div key={p.key} className="flex items-center justify-between">
                      <span className="text-sm">{p.label}</span>
                      <Switch
                        checked={!!form.permissions[p.key]}
                        onCheckedChange={() => togglePerm(p.key)}
                        data-testid={`perm-${p.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              İptal
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
